import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Table from "@/components/ui/table";
import type { TableColumn } from "@/types/component";
import ReportDetailLayout from "../../components/ReportDetailLayout";
import ReportFilterBar, {
  type ReportFilterState,
} from "../../components/ReportFilterBar";
import ReportKPICard from "../../components/ReportKPICard";
import ReportChart from "../../components/ReportChart";
import ReportExportButtons from "../../components/ReportExportButtons";
import { useReportData } from "../../hooks/useReportData";
import { useReportCategories } from "../../hooks/useReportCategories";
import { useReportConfig } from "../../hooks/useReportConfig";
import {
  reportsExtendedApi,
  type BudgetVarianceResponse,
  type BudgetVarianceItem,
  type BudgetVarianceParams,
} from "../../services/reports-extended.api";
import { Finance } from "@/components/icons";
import styles from "./BudgetReportPage.module.scss";
import { fetchDepartments } from "@/features/announcements/services/departments.api";

const PAGE_SIZE = 20;
/** Kullanıcı ayarlarının (kolon düzeni, filtre setleri, zamanlama) saklandığı anahtar. */
const REPORT_KEY = "BUDGET_VARIANCE";

const formatCurrency = (v: number) =>
  new Intl.NumberFormat("tr-TR", {
    style: "currency",
    currency: "TRY",
    maximumFractionDigits: 0,
  }).format(v);

const BudgetReportPage: React.FC = () => {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<ReportFilterState>({});
  const [departments, setDepartments] = useState<
    { id: string; name: string }[]
  >([]);

  useEffect(() => {
    let cancelled = false;
    fetchDepartments({ limit: 100 })
      .then((list) => {
        if (!cancelled) {
          setDepartments(list.map((d) => ({ id: d.id, name: d.name })));
        }
      })
      .catch(() => {
        if (!cancelled) setDepartments([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);
  const [approvedOnly, setApprovedOnly] = useState<boolean>(false);
  const [page, setPage] = useState(1);

  const categories = useReportCategories();

  const params: BudgetVarianceParams = useMemo(
    () => ({
      page,
      limit: PAGE_SIZE,
      dateFrom: filter.dateFrom,
      departmentID: filter.departmentID || undefined,
      dateTo: filter.dateTo,
      search: filter.search?.trim() || undefined,
      categoryIds: filter.categoryIds,
      approvedOnly,
    }),
    [filter, approvedOnly, page],
  );

  const { data, isLoading, error } = useReportData<
    BudgetVarianceResponse,
    BudgetVarianceParams
  >({
    fetcher: (p, signal) => reportsExtendedApi.getBudgetVariance(p, signal),
    params,
  });

  const summary = data?.summary;
  const items = data?.items ?? [];
  const count = data?.count ?? 0;

  const applyPresetFilters = (raw: Record<string, unknown>) => {
    const { approvedOnly: presetApproved, ...rest } = raw as ReportFilterState & {
      approvedOnly?: boolean;
    };
    setFilter(rest);
    setApprovedOnly(!!presetApproved);
    setPage(1);
  };

  const topByBudget = [...items]
    .sort((a, b) => b.plannedBudget - a.plannedBudget)
    .slice(0, 10);

  // Kolonların `key`/`label` alanları yapılandırma içindir: kullanıcının kolon
  // seçme/gizleme ve sıralama tercihi bu anahtarlarla saklanır (R11). Başlık
  // metni çeviriye göre değiştiği için anahtar olarak kullanılamaz.
  const baseColumns: TableColumn<BudgetVarianceItem>[] = [
    {
      key: "title",
      label: t("reports.budget.headerTraining"),
      // Satırı tanımlayan kolon gizlenemez; aksi halde tablo okunamaz hale gelir.
      alwaysVisible: true,
      header: t("reports.budget.headerTraining"),
      accessor: (row) => (
        <span className={styles.titleCell}>
          {row.title}
          {row.isBudgetApproved && (
            <span className={styles.approvedBadge}>{t("reports.budget.approved")}</span>
          )}
        </span>
      ),
    },
    {
      key: "plannedBudget",
      label: "PLANLANAN",
      header: "PLANLANAN",
      accessor: (row) => formatCurrency(row.plannedBudget),
    },
    {
      key: "actualCost",
      label: t("reports.budget.headerActual"),
      header: t("reports.budget.headerActual"),
      accessor: (row) => formatCurrency(row.actualCost),
    },
    {
      key: "enrollmentCount",
      label: "KAYIT",
      header: "KAYIT",
      accessor: (row) => row.enrollmentCount,
    },
    {
      key: "completedCount",
      label: "TAMAMLAYAN",
      header: "TAMAMLAYAN",
      accessor: (row) => row.completedCount,
    },
    {
      key: "costPerParticipant",
      label: t("reports.budget.headerPerPerson"),
      header: t("reports.budget.headerPerPerson"),
      accessor: (row) => formatCurrency(row.costPerParticipant),
    },
    {
      key: "variancePercent",
      label: "SAPMA",
      header: "SAPMA",
      accessor: (row) => (
        <span
          className={
            row.variancePercent > 5
              ? styles.varianceBad
              : row.variancePercent < -5
                ? styles.varianceGood
                : styles.varianceNeutral
          }
        >
          {row.variancePercent >= 0 ? "+" : ""}
          {row.variancePercent.toFixed(1)}%
        </span>
      ),
    },
  ];

  // R11 yapılandırılabilirliği: kolon ayarları, filtre setleri, zamanlama ve
  // sayfa blok düzeni tek çağrıda bağlanıyor.
  const cfg = useReportConfig<BudgetVarianceItem>({
    reportKey: REPORT_KEY,
    reportTitle: t("reports.budget.title"),
    columns: baseColumns,
  });

  const currentFilters = { ...filter, approvedOnly };

  useEffect(() => {
    if (!cfg.defaultFilters) return;
    applyPresetFilters(cfg.defaultFilters);
  }, [cfg.defaultFilters]);

  return (
    <ReportDetailLayout
      title={t("reports.budget.title")}
      categoryLabel={t("reportCatalog.categories.financial.title")}
      description={t("reports.budget.description")}
      layoutKey={`report:${REPORT_KEY}`}
      exportSlot={
        <div className={styles.toolbar}>
          {cfg.toolbar}
          <ReportExportButtons
            reportType="BUDGET_VARIANCE"
            filters={params as unknown as Record<string, unknown>}
            filenameBase="butce-gerceklesme"
            exportFn={reportsExtendedApi.exportReport}
            disabled={isLoading}
          />
        </div>
      }
      filterSlot={
        <ReportFilterBar
          categories={categories}
          showCategory
          value={filter}
          onChange={(next) => {
            setFilter(next);
            setPage(1);
          }}
          departments={departments}
          extras={
            <label className={styles.approvedToggle}>
              <input
                type="checkbox"
                checked={approvedOnly}
                onChange={(e) => {
                  setApprovedOnly(e.target.checked);
                  setPage(1);
                }}
              />
              <span>{t("reports.budget.onlyApproved")}</span>
            </label>
          }
        />
      }
    >
      {cfg.presetBar(currentFilters, applyPresetFilters)}

      {/* Bloklar kullanıcının kaydettiği sıraya göre; gizlenenler hiç render edilmez (R11). */}
      {renderKpiBlock()}
      {renderChartBlock()}
      {renderTableBlock()}

      {cfg.scheduleModal(currentFilters)}
    </ReportDetailLayout>
  );

  function renderKpiBlock() {
    return (
      <div key="kpi" className={styles.kpiGrid}>
        <ReportKPICard
          title="Toplam Planlanan"
          value={summary ? formatCurrency(summary.totalPlanned) : "—"}
          icon={<Finance width={18} height={18} />}
        />
        <ReportKPICard
          title={t("reports.budget.totalActual")}
          value={summary ? formatCurrency(summary.totalActual) : "—"}
          icon={<Finance width={18} height={18} />}
          accent="success"
        />
        <ReportKPICard
          title="Genel Sapma"
          value={
            summary
              ? `${summary.overallVariancePercent >= 0 ? "+" : ""}${summary.overallVariancePercent.toFixed(1)}%`
              : "—"
          }
          icon={<Finance width={18} height={18} />}
          accent={
            summary && summary.overallVariancePercent > 5
              ? "danger"
              : summary && summary.overallVariancePercent < -5
                ? "success"
                : "warning"
          }
        />
        <ReportKPICard
          title={t("reports.budget.trainingCount")}
          value={summary?.educationCount ?? 0}
          icon={<Finance width={18} height={18} />}
        />
      </div>
    );
  }

  function renderChartBlock() {
    return (
      <ReportChart
        key="chart"
        title={t("reports.budget.topBudgeted")}
        subtitle={t("reports.budget.subtitle")}
        type="bar"
        isLoading={isLoading}
        isEmpty={topByBudget.length === 0}
        series={[
          {
            name: "Planlanan",
            data: topByBudget.map((i) => i.plannedBudget),
          },
          {
            name: t("reports.budget.actual"),
            data: topByBudget.map((i) => i.actualCost),
          },
        ]}
        options={{
          xaxis: { categories: topByBudget.map((i) => i.title) },
          colors: ["#4f46e5", "#10b981"],
          yaxis: {
            labels: {
              formatter: (val: number) => formatCurrency(val),
            },
          },
        }}
      />
    );
  }

  function renderTableBlock() {
    return (
      <React.Fragment key="table">
        {cfg.columnSettings}
        <Table
          columns={cfg.columns}
          data={items}
          isLoading={isLoading}
          getRowId={(row) => row.educationID}
          totalRecords={count}
          currentPage={page}
          pageSize={PAGE_SIZE}
          onPageChange={setPage}
          emptyMessage={
            error ? t("reports.budget.noData") : t("reports.budget.noBudgeted")
          }
        />
      </React.Fragment>
    );
  }
};

export default BudgetReportPage;
